import { ReactElement } from 'react';
import { SingleValue } from 'react-select';
import { Checkbox, JsonEditor, Select, Switch } from '.';
import { SettingComponentType, Setting as SettingModel } from '../types';
import { SelectOptionProps } from './Select';

export type SettingFieldProps = {
  setting: SettingModel;
  pending?: boolean;
  onChange?: (value: any) => void;
};

export function SettingField({
  setting,
  pending,
  onChange,
}: SettingFieldProps): ReactElement {
  switch (setting.component) {
    case SettingComponentType.Checkbox:
      return (
        <Checkbox
          id={`setting-field-${setting.key}`}
          name={`setting-field-${setting.key}`}
          value={!!setting.value}
          onChange={onChange}
          disabled={pending}
        />
      );

    case SettingComponentType.Switch:
      return (
        <Switch
          id={`setting-field-${setting.key}`}
          name={`setting-field-${setting.key}`}
          value={!!setting.value}
          onChange={onChange}
          loading={pending}
          disabled={pending}
        />
      );

    case SettingComponentType.TextInput:
      return (
        <input
          type="text"
          id={`setting-field-${setting.key}`}
          name={`setting-field-${setting.key}`}
          value={setting.value ?? ''}
          onChange={e => onChange?.(e.target.value)}
          disabled={pending}
        />
      );

    case SettingComponentType.TextArea:
      return (
        <textarea
          id={`setting-field-${setting.key}`}
          name={`setting-field-${setting.key}`}
          value={setting.value ?? ''}
          onChange={e => onChange?.(e.target.value)}
          disabled={pending}
        />
      );

    case SettingComponentType.NumericInput:
      return (
        <input
          type="number"
          id={`setting-field-${setting.key}`}
          name={`setting-field-${setting.key}`}
          value={setting.value ?? ''}
          onChange={e => onChange?.(e.target.value)}
          disabled={pending}
        />
      );

    case SettingComponentType.Select:
      const options: SelectOptionProps<string>[] =
        setting.schema?.enum?.map((value: string) => ({
          label: value,
          value,
        })) ?? [];

      return (
        <Select
          id={`setting-field-${setting.key}`}
          name={`setting-field-${setting.key}`}
          options={options}
          value={setting.value ?? ''}
          onChange={
            ((selected: SingleValue<SelectOptionProps<string>>) => {
              if (selected) {
                onChange?.(selected.value);
              }
            }) as any
          }
          disabled={pending}
        />
      );

    case SettingComponentType.JsonEditor:
      return (
        <JsonEditor
          name={`setting-field-${setting.key}`}
          value={setting.value}
          schema={setting.schema ?? undefined}
          allowEmpty
          onChange={(v: any, e?: string) => {
            if (!e) {
              onChange?.(v);
            }
          }}
          disabled={pending}
        />
      );

    default:
      return <></>;
  }
}
